import React, { useState } from 'react';
import { 
  Card,
  CardContent,
  Typography, 
  Box, 
  Grid,
  Chip,
  Rating,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert
} from '@mui/material';
import { CalendarToday as CalendarIcon, Star as StarIcon, Flag as FlagIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { logEvent } from 'firebase/analytics';
import { deleteDoc, doc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { analytics, db } from '../api/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const ReviewListItem = ({ review, showDelete = false, currentUserId = null, onReviewDeleted = null }) => {
  const { t, isArabic } = useLanguage();
  const { user } = useAuth();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [isDeletingReview, setIsDeletingReview] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  if (!review) return null;

  const formattedTimestamp = review.createdAt?.toDate ? 
    review.createdAt.toDate().toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) : 
    t('Invalid Date', 'تاريخ غير صحيح');

  const criteriaLabels = {
    teachingQuality: t('Teaching', 'التدريس'),
    treatment: t('Treatment', 'المعاملة'),
    gradingAssessment: t('Grading', 'التقييم'),
    tests: t('Tests', 'الاختبارات'),
    attendance: t('Attendance', 'الحضور'),
    // Legacy criteria for backward compatibility
    knowledgeExpertise: t('Knowledge', 'المعرفة'), 
    communication: t('Communication', 'التواصل'),
    assessment: t('Assessment', 'التقييم'),
    studentEngagement: t('Engagement', 'الإشراك')
  };

  // Handle both new and legacy review formats (all should be 1-5 scale now)
  const displayRating = review.overallRating || review.rating || 0;
  const isLegacyReview = !review.overallRating && review.rating;

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: { en: 'Poor', ar: 'ضعيف' },
      2: { en: 'Fair', ar: 'مقبول' },
      3: { en: 'Good', ar: 'جيد' },
      4: { en: 'Very Good', ar: 'جيد جداً' },
      5: { en: 'Excellent', ar: 'ممتاز' }
    };
    return isArabic ? labels[Math.round(rating)]?.ar : labels[Math.round(rating)]?.en;
  };

  const handleReportClick = () => {
    setReportDialogOpen(true);
  };

  const handleReportClose = () => {
    setReportDialogOpen(false);
    setReportReason('');
    setReportSuccess(false);
    setSubmitError(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    setIsDeletingReview(true);
    setDeleteError(null);
    
    try {
      // Delete the review from Firestore
      await deleteDoc(doc(db, 'reviews', review.id));
      
      // Track deletion
      logEvent(analytics, 'delete_review', {
        review_id: review.id,
        professor_id: review.professorId,
        overall_rating: review.overallRating || review.rating
      });
      
      // Notify parent component
      if (onReviewDeleted) {
        onReviewDeleted(review.id);
      }
      
      // Close dialog
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting review:', error);
      setDeleteError(t('Failed to delete review. Please try again.', 'فشل في حذف التقييم. يرجى المحاولة مرة أخرى.'));
    } finally {
      setIsDeletingReview(false);
    }
  };

  // Check if current user owns this review
  const isOwner = currentUserId && review.userId === currentUserId;

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) {
      return; // Don't submit empty reports
    }

    if (!user) {
      setSubmitError(t('You must be signed in to report a review.', 'يجب أن تكون مسجل الدخول للإبلاغ عن تقييم.'));
      return;
    }

    setIsSubmittingReport(true);
    try {
      // Save report to Firestore
      await addDoc(collection(db, 'reports'), {
        reviewId: review.id,
        professorId: review.professorId,
        reportedBy: user.uid,
        reason: reportReason.trim(),
        createdAt: serverTimestamp(),
        status: 'pending', // 'pending', 'reviewed', 'resolved'
        reviewSnapshot: {
          overallRating: review.overallRating || review.rating,
          comment: review.comment || review.reviewText || '',
          userId: review.userId
        }
      });

      // Track report submission
      logEvent(analytics, 'report_review', {
        review_id: review.id,
        reason_length: reportReason.trim().length,
        review_rating: review.overallRating || review.rating
      });
      
      setReportSuccess(true);
      setTimeout(() => {
        handleReportClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmitError(t('Failed to submit report. Please try again.', 'فشل في إرسال البلاغ. يرجى المحاولة مرة أخرى.'));
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return (
    <Card 
      elevation={2} 
      sx={{ 
        mb: 3, 
        borderRadius: 2,
        bgcolor: '#f8faff',
        transition: 'all 0.3s ease-in-out',
        '&:hover': { 
          elevation: 4,
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          bgcolor: '#f0f7ff'
        },
        direction: isArabic ? 'rtl' : 'ltr'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with Rating and Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating 
                value={displayRating} 
                readOnly 
                size="small"
                icon={<StarIcon fontSize="inherit" />}
                sx={{
                  direction: 'ltr',
                  '& .MuiRating-icon': {
                    direction: 'ltr'
                  }
                }}
              />
              <Typography variant="h6" color="primary" fontWeight="bold">
                {displayRating.toFixed(1)}/5
              </Typography>
            </Box>
            <Chip 
              label={getRatingLabel(displayRating)} 
              size="small"
              color={getRatingColor(displayRating)}
              variant="filled"
            />
            {isLegacyReview && (
              <Chip 
                label={t('Legacy', 'قديم')} 
                size="small" 
                variant="outlined" 
                color="secondary" 
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <CalendarIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">
                {formattedTimestamp}
              </Typography>
            </Box>
            {showDelete && isOwner && (
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'error.main',
                    backgroundColor: 'error.lighter'
                  }
                }}
                title={t('Delete your review', 'حذف تقييمك')}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleReportClick}
              sx={{ 
                color: 'text.secondary',
                '&:hover': { 
                  color: 'error.main',
                  backgroundColor: 'error.lighter'
                }
              }}
              title={t('Report inappropriate content', 'الإبلاغ عن محتوى غير مناسب')}
            >
              <FlagIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Review Comment */}
        {(review.comment || review.reviewText) && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ 
                fontStyle: 'italic',
                lineHeight: 1.6,
                color: 'text.primary',
                backgroundColor: 'grey.50',
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                '&:before': {
                  content: '"\\201C"',
                  position: 'absolute',
                  top: 0,
                  left: isArabic ? 'auto' : 10,
                  right: isArabic ? 10 : 'auto',
                  fontSize: '4rem',
                  lineHeight: 1,
                  color: 'primary.main',
                  opacity: 0.1,
                  fontFamily: 'serif',
                }
              }}>
                {review.comment || review.reviewText}
              </Typography>
            </Box>
          </>
        )}
        
        {/* Detailed Ratings */}
        {review.ratings && Object.keys(review.ratings).some(key => review.ratings[key] > 0) && (
          <>
            <Divider sx={{ my: 2, borderColor: 'rgba(25, 118, 210, 0.12)' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                {t('Detailed Ratings', 'التقييمات التفصيلية')}
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(review.ratings).map(([key, value]) => (
                  value > 0 && (
                    <Grid item key={key}>
                      <Chip 
                        label={`${criteriaLabels[key]}: ${value}/5`}
                        size="small"
                        variant="filled"
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: 'grey.100',
                          border: '1px solid',
                          borderColor: 'divider',
                          '& .MuiChip-label': {
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }
                        }}
                      />
                    </Grid>
                  )
                ))}
              </Grid>
            </Box>
          </>
        )}

        {/* Anonymous Attribution */}
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'rgba(25, 118, 210, 0.12)' }}>
          <Typography variant="caption" color="text.secondary">
            {t('Anonymous Student Review', 'تقييم طالب مجهول')}
          </Typography>
        </Box>
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('Delete Review', 'حذف التقييم')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {t('Are you sure you want to delete this review? This action cannot be undone.', 'هل أنت متأكد من حذف هذا التقييم؟ لا يمكن التراجع عن هذا الإجراء.')}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleDeleteClose}
            disabled={isDeletingReview}
          >
            {t('Cancel', 'إلغاء')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeletingReview}
          >
            {isDeletingReview ? t('Deleting...', 'جاري الحذف...') : t('Delete', 'حذف')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={handleReportClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('Report Review', 'الإبلاغ عن التقييم')}
        </DialogTitle>
        <DialogContent>
          {reportSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              {t('Thank you for your report. We will review it shortly.', 'شكراً لك على الإبلاغ. سنراجعه قريباً.')}
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                {t('Tell us what\'s wrong with this review:', 'أخبرنا ما الخطأ في هذا التقييم:')}
              </Typography>
              <TextField
                autoFocus
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder={t('Please describe the issue...', 'يرجى وصف المشكلة...')}
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                disabled={isSubmittingReport}
              />
              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleReportClose}
            disabled={isSubmittingReport}
          >
            {t('Cancel', 'إلغاء')}
          </Button>
          {!reportSuccess && (
            <Button 
              onClick={handleReportSubmit}
              variant="contained"
              color="error"
              disabled={isSubmittingReport || !reportReason.trim()}
            >
              {isSubmittingReport ? t('Submitting...', 'جاري الإرسال...') : t('Submit', 'إرسال')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ReviewListItem; 